'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { CentText } from '@/lib/components/Text/CentText';
import { FloatText } from '@/lib/components/Text/FloatText';
import { USDText } from '@/lib/components/Text/USDText';
import { cn } from '@/lib/ui-kit/utils';
import {
  calculateAvgMarketBuyPrice,
  calculateAvgMarketSellPrice,
} from '@/lib/utils/calculateAvgMarketPrice';
import { PriceVolumeCumulative } from '@/lib/utils/getWithCumulativeTotals';

type HoverSummaryOverlayProps = {
  hoveredItem: PriceVolumeCumulative | null;
  cumulativeAsks: PriceVolumeCumulative[];
  cumulativeBids: PriceVolumeCumulative[];
  tooltipX: number;
  tooltipY: number;
};

export const HoverSummaryOverlay = ({
  tooltipX,
  tooltipY,
  hoveredItem,
  cumulativeAsks,
  cumulativeBids,
}: HoverSummaryOverlayProps) => {
  const t = useTranslations('orderBook');
  const hoveredSummary = useMemo(() => {
    if (!hoveredItem) {
      return null;
    }

    // Match by price (unique within a side) — the hoveredItem reference becomes
    // stale on every socket update, since cumulativeAsks/Bids are rebuilt with
    // fresh objects. Using indexOf here would return -1 after an update and
    // collapse the avg-price calculation to a single (or empty) row.
    const source = hoveredItem.side === 'asks' ? cumulativeAsks : cumulativeBids;
    const itemIndex = source.findIndex((row) => row.price.equals(hoveredItem.price));
    if (itemIndex === -1) {
      return null;
    }
    const current = source[itemIndex];

    const avgPrice =
      hoveredItem.side === 'asks'
        ? calculateAvgMarketBuyPrice(source.slice(itemIndex))
        : calculateAvgMarketSellPrice(source.slice(0, itemIndex + 1));

    return {
      avgPrice,
      totalShares: current.cumulativeShares,
      totalVolume: current.cumulativeVolume,
    };
  }, [cumulativeAsks, cumulativeBids, hoveredItem]);

  return (
    <div className="pointer-events-none fixed inset-0 z-50 h-screen w-screen">
      {hoveredItem && hoveredSummary ? (
        <div
          className={cn(
            'absolute flex w-[200px] flex-col gap-2 rounded-[10px] border border-v2-border bg-v2-surface-secondary p-3 shadow-v2-card',
            { ['-translate-y-full']: hoveredItem.side === 'bids' },
          )}
          style={{ top: `${tooltipY}px`, left: `${tooltipX}px` }}
        >
          <div className="flex w-full items-center justify-between gap-2 text-[13px] leading-[1.5]">
            <span className="text-v2-text-secondary">{t('avgPrice')}</span>
            <span className="text-v2-text-primary">
              ≈<CentText value={hoveredSummary.avgPrice} decimals={2} />
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-2 text-[13px] leading-[1.5]">
            <span className="text-v2-text-secondary">{t('totalShares')}</span>
            <span className="text-v2-text-primary">
              <FloatText value={hoveredSummary.totalShares} decimals={2} />
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-2 text-[13px] leading-[1.5]">
            <span className="text-v2-text-secondary">{t('totalAmount')}</span>
            <span className="text-v2-text-primary">
              <USDText value={hoveredSummary.totalVolume} />
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};
